import { Box, Checkbox, CircularProgress, Dialog, DialogContent, DialogTitle, FormControlLabel, IconButton, Typography } from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import cx from 'classnames';
import { FormikProps } from 'formik';
import React, { useRef, useState } from 'react';
import PrismaZoom from 'react-prismazoom';
import { translate } from '../../../../common/intl';
import { AsyncState, AsyncStatus } from '../../../../state/types';
import { styles as viewFileStyles } from '../../../components/common/view.file/utils/styles';
import { useStyles } from './WhiteLabelingManagement.styles';

interface Props extends FormikProps<any>{
  onLogoChange: (event: React.FormEvent<HTMLInputElement>) => void
  onAvatarChange: (event: React.FormEvent<HTMLInputElement>) => void
  onPoLogoChange: (event: React.FormEvent<HTMLInputElement>) => void
  onPoLogoSameAsLogo: (checked: boolean) => void
  asyncState: AsyncState
  asyncS3linkState: AsyncState
  whiteLabel: { logo: string, avatar: string, poLogo?: string }
}

interface FileUploadInfo {
  name: string
  size: string
}

const WhiteLabelingManagementPresenter: React.FunctionComponent<Props> = ({
  dirty,
  resetForm,
  initialValues,
  onLogoChange,
  onAvatarChange,
  onPoLogoChange,
  onPoLogoSameAsLogo,
  asyncState,
  asyncS3linkState,
  whiteLabel,
}) => {
  const classes = useStyles();
  const viewFileClasses = viewFileStyles();
  const logoInputRef = useRef<HTMLInputElement>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const poLogoInputRef = useRef<HTMLInputElement>(null);
  const [logoUploadedFile, setLogoUploadedFile] = useState<FileUploadInfo | null>(null);
  const [avatarUploadedFile, setAvatarUploadedFile] = useState<FileUploadInfo | null>(null);
  const [poLogoUploadedFile, setPoLogoUploadedFile] = useState<FileUploadInfo | null>(null);
  const [isPoLogoSameAsLogo, setIsPoLogoSameAsLogo] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handlePoLogoSameAsLogoChange = (checked: boolean) => {
    setIsPoLogoSameAsLogo(checked);
    onPoLogoSameAsLogo(checked);
  };

  const handleUploadClick = (inputRef: React.RefObject<HTMLInputElement>) => {
    inputRef.current?.click();
  };

  const createFileChangeHandler = (
    setUploadedFile: React.Dispatch<React.SetStateAction<FileUploadInfo | null>>,
    onChange: (event: React.FormEvent<HTMLInputElement>) => void,
  ) => {
    return (event: React.FormEvent<HTMLInputElement>) => {
      const input = event.currentTarget;
      const file = input.files?.[0];
      if (!file) {
        return;
      }

      const fileSizeKB = (file.size / 1024).toFixed(0);
      setUploadedFile({
        name: file.name,
        size: `${fileSizeKB} KB`,
      });
      onChange(event);
    };
  };

  const getFileExtension = (fileName: string) => {
    return fileName.split('.').pop()?.toUpperCase() || 'FILE';
  };

  const isLoading = asyncState?.status === AsyncStatus.Active || asyncS3linkState?.status === AsyncStatus.Active;

  const renderUploadSection = (
    title: string,
    uploadedFile: FileUploadInfo | null,
    inputRef: React.RefObject<HTMLInputElement>,
    onChange: (event: React.FormEvent<HTMLInputElement>) => void,
    s3link: string | undefined,
    setUploadedFile: React.Dispatch<React.SetStateAction<FileUploadInfo | null>>,
  ) => {
    const handleFileChange = createFileChangeHandler(
      setUploadedFile,
      onChange,
    );

    return (
      <Box mb={2}>
        <Typography className={classes.label}>
          {title}
        </Typography>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/svg+xml,.svg"
          onChange={handleFileChange}
          className={classes.fileInput}
          title={translate('administration.company.information.whiteLabeling.chooseFile')}
          aria-label={translate('administration.company.information.whiteLabeling.chooseFile')}
          disabled={isLoading}
        />

        {uploadedFile ? (
          <Box>
            <Box className={classes.fileInfo}>
              <Box className={classes.fileIcon}>
                {isLoading ? (
                  <CircularProgress size={16} thickness={4} />
                ) : (
                  getFileExtension(uploadedFile.name)
                )}
              </Box>
              <Box className={classes.fileDetails}>
                <Typography className={classes.fileName}>
                  {uploadedFile.name}
                </Typography>
                <Typography className={classes.fileSize}>
                  {uploadedFile.size}
                </Typography>
              </Box>
            </Box>
            {!isLoading && (
              <Box
                className={classes.uploadArea}
                onClick={() => handleUploadClick(inputRef)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleUploadClick(inputRef);
                  }
                }}
                aria-label={translate('administration.company.information.whiteLabeling.replaceAriaLabel', { title: title.toLowerCase() })}
              >
                <Typography className={classes.uploadText}>
                  {translate('administration.company.information.whiteLabeling.replace')}
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box
            className={classes.uploadArea}
            onClick={() => handleUploadClick(inputRef)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleUploadClick(inputRef);
              }
            }}
            aria-label={translate('administration.company.information.whiteLabeling.uploadAriaLabel', { title: title.toLowerCase() })}
          >
            <Typography className={classes.uploadIcon}>📁</Typography>
            <Typography className={classes.uploadText}>
              {translate('administration.company.information.whiteLabeling.dragDrop', { title: title.toLowerCase() })}
            </Typography>
            <Typography className={classes.uploadSubtext}>
              {translate('administration.company.information.whiteLabeling.fileTypes')}
            </Typography>
          </Box>
        )}

        {s3link && (
          <Box mt={2}>
            <Typography variant="caption" color="textSecondary">
              {translate('administration.company.information.whiteLabeling.currentFile')}
            </Typography>
            <Typography variant="body2" style={{ marginTop: 4 }}>
              <a
                href={s3link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.preventDefault();
                  setPreviewUrl(s3link);
                }}
              >
                {s3link.split('/').pop()}
              </a>
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box>
      {renderUploadSection(
        translate('administration.company.information.whiteLabeling.logo.upload'),
        logoUploadedFile,
        logoInputRef,
        onLogoChange,
        whiteLabel.logo,
        setLogoUploadedFile,
      )}
      {renderUploadSection(
        translate('administration.company.information.whiteLabeling.avatar.upload'),
        avatarUploadedFile,
        avatarInputRef,
        onAvatarChange,
        whiteLabel.avatar,
        setAvatarUploadedFile,
      )}
      <Box>
        {renderUploadSection(
          translate('administration.company.information.whiteLabeling.poLogo.upload'),
          poLogoUploadedFile,
          poLogoInputRef,
          onPoLogoChange,
          whiteLabel.poLogo,
          setPoLogoUploadedFile,
        )}
        <FormControlLabel
          control={
            <Checkbox
              checked={isPoLogoSameAsLogo}
              onChange={(event) => handlePoLogoSameAsLogoChange(event.target.checked)}
              disabled={isLoading || !whiteLabel.logo}
            />
          }
          label={translate('administration.company.information.whiteLabeling.poLogo.sameAsLogo')}
        />
      </Box>

      <Dialog
        onClose={() => setPreviewUrl(null)}
        open={Boolean(previewUrl)}
        classes={{ paper: viewFileClasses.paper }}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle className={cx(viewFileClasses.title, classes.previewTitle)}>
          {previewUrl?.split('/').pop()}
          <IconButton
            href={previewUrl || ''}
            download={previewUrl?.split('/').pop()}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={translate('administration.company.information.whiteLabeling.download')}
            className={classes.previewDownloadButton}
          >
            <GetAppIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          classes={{ root: viewFileClasses.contentRoot }}
          onClick={(e) => {
            if ((e.target as HTMLElement).tagName === 'DIV') {
              setPreviewUrl(null);
            }
          }}
        >
          {previewUrl && (
            <PrismaZoom className={viewFileClasses.zoom} topBoundary={180}>
              <img src={previewUrl} alt={previewUrl} width="100%" />
            </PrismaZoom>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default WhiteLabelingManagementPresenter;
